"use client";

import { motion, useReducedMotion } from "framer-motion";
import {
	ArrowRight,
	Check,
	Mail,
	MessageSquare,
	Phone,
	Store,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/ui/elements";

const EASE = [0.16, 1, 0.3, 1] as const;

// The hero leads with the boutique-studio wedge: follow-up that runs itself.
// We show only the positive Helios artifact (sample data): one intro-offer lead
// being followed up automatically across text, email, and voice while the team
// is teaching, ending in a booked first class. Names are illustrative.
const STEPS = [
	{
		icon: MessageSquare,
		label: "Texted back instantly",
		meta: "4s after signup",
	},
	{ icon: Mail, label: "Intro-offer reminder", meta: "Day 2, no reply" },
	{ icon: Phone, label: "Pre-class call task", meta: "Day 3" },
] as const;

function HeroCopy() {
	const reduceMotion = useReducedMotion();
	const item = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
	};
	const container = {
		hidden: {},
		visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
	};
	const containerProps = reduceMotion
		? {}
		: {
				initial: "hidden" as const,
				animate: "visible" as const,
				variants: container,
			};
	const iv = reduceMotion ? undefined : item;

	return (
		<motion.div
			className="flex max-w-xl flex-col items-center text-center xl:items-start xl:text-left"
			{...containerProps}
		>
			<motion.span
				variants={iv}
				className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
			>
				<Store className="size-4" />
				Built for boutique studios
			</motion.span>

			<motion.h1
				variants={iv}
				className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-[2.75rem] lg:text-5xl"
			>
				Turn intro offers into members, automatically
			</motion.h1>

			<motion.p
				variants={iv}
				className="mt-6 max-w-lg text-balance leading-relaxed text-muted-foreground sm:text-lg"
			>
				You can't teach a class and text every lead back at the same time.
				Helios follows up with every lead and member for you, across text,
				email, and voice, so none go cold while you're on the floor.
			</motion.p>

			<motion.div
				variants={iv}
				className="mt-8 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row xl:items-start"
			>
				<Button asChild size="xl" className="group w-full sm:w-auto">
					<Link href="/book-a-demo">
						Book a demo
						<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
					</Link>
				</Button>
				<Button
					asChild
					size="xl"
					variant="outline"
					className="w-full sm:w-auto"
				>
					<Link href="#follow-up">See it in action</Link>
				</Button>
			</motion.div>
		</motion.div>
	);
}

function FollowUpCard() {
	const reduceMotion = useReducedMotion();
	const rowAnim = (i: number) =>
		reduceMotion
			? {}
			: {
					initial: { opacity: 0, y: 10 },
					animate: { opacity: 1, y: 0 },
					transition: { duration: 0.6, ease: EASE, delay: 0.5 + i * 0.14 },
				};
	const outcomeAnim = reduceMotion
		? {}
		: {
				initial: { opacity: 0, y: 10 },
				animate: { opacity: 1, y: 0 },
				transition: {
					duration: 0.6,
					ease: EASE,
					delay: 0.5 + STEPS.length * 0.14,
				},
			};

	return (
		<div className="w-[300px] rounded-2xl border border-border bg-card p-5 shadow-xl sm:w-[332px]">
			<div className="flex items-center gap-2 border-b border-border pb-4">
				<Avatar
					name="Maya Rivera"
					src="/images/website/customers/maya-rivera.jpg"
					size="sm"
				/>
				<div className="min-w-0 flex-1">
					<div className="text-sm font-bold leading-tight text-foreground">
						Maya Rivera
					</div>
					<div className="text-xs leading-tight text-muted-foreground">
						Intro offer, new lead
					</div>
				</div>
				<span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
					Auto follow-up
				</span>
			</div>

			<ul className="mt-4 space-y-2.5">
				{STEPS.map(({ icon: Icon, label, meta }, i) => (
					<motion.li
						key={label}
						className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2"
						{...rowAnim(i)}
					>
						<span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50 text-muted-foreground">
							<Icon className="size-3.5" />
						</span>
						<div className="min-w-0 flex-1">
							<div className="truncate text-sm font-semibold text-foreground">
								{label}
							</div>
							<div className="truncate text-xs text-muted-foreground">
								{meta}
							</div>
						</div>
						<Check
							strokeWidth={3}
							className="size-4 shrink-0 text-green-600 dark:text-green-500"
						/>
					</motion.li>
				))}
			</ul>

			<motion.div
				className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-green-600/20 bg-green-600/5 px-3 py-2.5 text-center text-xs font-semibold text-green-700 dark:border-green-500/20 dark:text-green-500"
				{...outcomeAnim}
			>
				<Check strokeWidth={3} className="size-3.5" />
				Booked her first class
			</motion.div>
		</div>
	);
}

function Stage() {
	const reduceMotion = useReducedMotion();
	const cardAnim = reduceMotion
		? {}
		: {
				initial: { opacity: 0, x: 24, scale: 0.98 },
				animate: { opacity: 1, x: 0, scale: 1 },
				transition: { duration: 0.8, ease: EASE, delay: 0.3 },
			};

	return (
		<div className="relative flex w-full justify-center xl:justify-end">
			{/* Faded duplicate behind for depth — purely decorative. */}
			<div
				aria-hidden
				className="absolute left-1/2 top-6 hidden h-full w-[300px] -translate-x-1/2 rounded-2xl border border-border bg-muted/30 sm:w-[332px] xl:left-auto xl:right-6 xl:block xl:translate-x-0"
			/>
			<motion.div {...cardAnim} className="relative z-10">
				<FollowUpCard />
			</motion.div>
		</div>
	);
}

export function StudiosHero() {
	return (
		<section className="relative overflow-x-clip border-b border-border bg-background pb-16 pt-28 sm:pb-20 lg:pt-36">
			<div className="mx-auto grid max-w-7xl items-center gap-16 px-4 lg:px-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)] xl:gap-10">
				<HeroCopy />
				<Stage />
			</div>
		</section>
	);
}
